import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR, { mutate as swrMutate } from 'swr';

import { useLoggedInEmployeeProfile } from '~/components/Auth';
import { getCompanyData } from '~/data/company';
import {
  getCountryByLegalEntity,
  useAllLegalEntities,
} from '~/data/legalEntity';
import {
  ReportingGroupType,
  TimeBlock,
  getAutoDeduction,
  getCountryHoursSettings,
  getPrevTimeCardWorkingHours,
  getTimeCards,
  updateTimeCard,
} from '~/data/worktime';
import { useCurrentDate } from '~/hooks/useCurrentDate';
import { useMutateInlineData } from '~/hooks/useMutateInlineData';

import { findRunningTimeBlock } from './findRunningTimeBlock';
import { momentIsFilled as checkIfMomentIsFilled } from './momentIsFilled';
import { timerJustStarted as checkIfTimerJustStarted } from './timerJustStarted';

export interface UseWorktimeSignInOutOutput {
  loading: boolean;
  failedToLoad: boolean;
  requiresToBeConfigured: boolean;
  runningTimeBlock?: TimeBlock;
  momentIsFilled: boolean;
  timerJustStarted: boolean;
  startingTimer: boolean;
  handleStartTimer: () => void;
  stoppingTimer: boolean;
  handleStopTimer: () => void;
  handleAddFullDay: () => void;
  addedTime: boolean;
  addedFullDay: boolean;
}

export const useWorktimeSignInOut = (): UseWorktimeSignInOutOutput => {
  const { data: companyData, error: companyDataError } = useSWR(
    'getCompanyData',
    getCompanyData,
  );
  const { legalEntities } = useAllLegalEntities();
  const employeeInfo = useLoggedInEmployeeProfile();
  const currentDate = useCurrentDate();

  const {
    data: timeCards,
    error: timeCardsError,
    mutate: mutateTimeCards,
  } = useSWR(
    ['getTimeCards', employeeInfo!.id, currentDate, currentDate],
    ([_, empId, startDate, endDate]) => getTimeCards(empId, startDate, endDate),
  );

  const [todaysTimeCard] = timeCards || [];

  const [runningTimeBlock, setRunningTimeBlock] = useState<TimeBlock>();
  const [momentIsFilled, setMomentIsFilled] = useState(false);
  const [timerJustStarted, setTimerJustStarted] = useState(false);

  const updateTimerStartStopIndicators = useCallback(() => {
    const actualRunningTimeBlock = findRunningTimeBlock(todaysTimeCard);

    setRunningTimeBlock(actualRunningTimeBlock);

    setMomentIsFilled(
      !actualRunningTimeBlock && checkIfMomentIsFilled(todaysTimeCard),
    );

    setTimerJustStarted(
      !!actualRunningTimeBlock && checkIfTimerJustStarted(todaysTimeCard),
    );
  }, [
    todaysTimeCard,
    setRunningTimeBlock,
    setMomentIsFilled,
    setTimerJustStarted,
  ]);

  // Recalculate timer start/stop indicators every second.
  // This way we make sure we can show right buttons with right status.
  useEffect(() => {
    updateTimerStartStopIndicators();

    const timeout = setInterval(() => updateTimerStartStopIndicators(), 1000);

    return () => {
      clearInterval(timeout);
    };
  }, [updateTimerStartStopIndicators]);

  const defaultWorkReportingType =
    companyData?.config.fComDefaultWorkReportingType;
  const getRequiredWorkingHours = useCallback(
    async () =>
      todaysTimeCard?.requiredWorkingHours ||
      (await getPrevTimeCardWorkingHours(employeeInfo!.id, currentDate)) ||
      (
        await getCountryHoursSettings(
          await getCountryByLegalEntity(
            employeeInfo!.legalEntity.id,
            legalEntities,
          ),
        )
      )?.workDayHours ||
      companyData?.config.fComTmWorkdayLengthHours ||
      'PT8H',
    [
      companyData?.config.fComTmWorkdayLengthHours,
      currentDate,
      employeeInfo,
      legalEntities,
      todaysTimeCard?.requiredWorkingHours,
    ],
  );

  const { t } = useTranslation();

  const getRequiredWorkingHoursWithoutDeduction = useCallback(async () => {
    let autoDeduction;
    let totalDuration;
    const requiredWorkingHours = await getRequiredWorkingHours();
    try {
      autoDeduction = await getAutoDeduction(employeeInfo!.id);
      const deductionDuration = moment.duration(autoDeduction);
      const workHoursDuration = moment.duration(requiredWorkingHours);
      totalDuration = moment.duration(
        workHoursDuration.asMilliseconds() + deductionDuration.asMilliseconds(),
      );
    } catch (e) {
      totalDuration = moment.duration(requiredWorkingHours);
    }
    return totalDuration.toISOString();
  }, [employeeInfo, getRequiredWorkingHours]);

  const [handleStartTimer, startingTimer] = useMutateInlineData(
    useCallback(async () => {
      const getWorkingHours = await getRequiredWorkingHours();
      const timeCard = await updateTimeCard(employeeInfo!.id, {
        date: currentDate,
        requiredWorkingHours: getWorkingHours,
        comment: todaysTimeCard?.comment || '',
        timeBlocks: [
          ...(todaysTimeCard?.timeBlocks || []),
          {
            reportingType: defaultWorkReportingType as string,
            reportingGroupType: ReportingGroupType.TIMESHEET_WORK_TYPE,
            from: moment().format('HH:mm:ss'),
            customData: {},
          },
        ],
      });

      await mutateTimeCards(() => [timeCard]);
    }, [
      getRequiredWorkingHours,
      employeeInfo,
      currentDate,
      todaysTimeCard?.comment,
      todaysTimeCard?.timeBlocks,
      defaultWorkReportingType,
      mutateTimeCards,
    ]),
    {
      error: t('failedToSignIn'),
      success: t('successfullySignedIn'),
    },
  );

  const [handleStopTimer, stoppingTimer] = useMutateInlineData(
    useCallback(async () => {
      const timeCard = await updateTimeCard(employeeInfo!.id, {
        ...todaysTimeCard,
        timeBlocks: todaysTimeCard.timeBlocks.map((timeBlock) => {
          if (timeBlock.from && !timeBlock.to) {
            return { ...timeBlock, to: moment().format('HH:mm:ss') };
          }

          return timeBlock;
        }),
      });

      await mutateTimeCards(() => [timeCard]);
      await swrMutate(['getTimeSheet', employeeInfo!.id]);
    }, [employeeInfo, todaysTimeCard, mutateTimeCards]),
    {
      error: t('failedToSignOut'),
      success: t('successfullySignedOut'),
    },
  );

  const [handleAddFullDay, addingFullDay] = useMutateInlineData(
    useCallback(async () => {
      const requiredWorkingHoursWithoutDeduction =
        await getRequiredWorkingHoursWithoutDeduction();
      const getWorkingHours = await getRequiredWorkingHours();
      const timeCard = await updateTimeCard(employeeInfo!.id, {
        date: currentDate,
        requiredWorkingHours: getWorkingHours,
        comment: todaysTimeCard?.comment || '',
        timeBlocks: [
          ...(todaysTimeCard?.timeBlocks || []),
          {
            reportingType: defaultWorkReportingType as string,
            reportingGroupType: ReportingGroupType.TIMESHEET_WORK_TYPE,
            hours: requiredWorkingHoursWithoutDeduction,
            customData: {},
          },
        ],
      });

      await mutateTimeCards(() => [timeCard]);
    }, [
      getRequiredWorkingHoursWithoutDeduction,
      getRequiredWorkingHours,
      employeeInfo,
      currentDate,
      todaysTimeCard?.comment,
      todaysTimeCard?.timeBlocks,
      defaultWorkReportingType,
      mutateTimeCards,
    ]),
    {
      error: t('failedToSignIn'),
      success: t('successfullySignedIn'),
    },
  );

  const addedTime = useMemo(
    () =>
      addingFullDay || (todaysTimeCard && todaysTimeCard.timeBlocks.length > 0),
    [addingFullDay, todaysTimeCard],
  );

  const addedFullDay = useMemo(() => {
    const requiredWorkingHours = todaysTimeCard?.requiredWorkingHours;

    return (
      todaysTimeCard &&
      todaysTimeCard.timeBlocks.some(
        (tb) => !tb.from && tb.hours === requiredWorkingHours,
      )
    );
  }, [todaysTimeCard]);

  return {
    loading:
      (!companyData && !companyDataError) || (!timeCards && !timeCardsError),
    failedToLoad: !!companyDataError || !!timeCardsError,
    requiresToBeConfigured: !defaultWorkReportingType,
    runningTimeBlock,
    momentIsFilled,
    timerJustStarted,
    startingTimer,
    handleStartTimer,
    stoppingTimer,
    handleStopTimer,
    handleAddFullDay,
    addedTime,
    addedFullDay,
  };
};
